import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    getCalibrationSchedulesCalendar,
    getOpportunitySchedulesCalendar,
    getLeadSchedulesCalendar,
    getProjectSubTasksCalendar,
    getTicketsCalendar,
    getJoinedGreenUsers,
    getAllFacilitiesForTickets,
} from '../../../Redux/actions';
import {
    Grid, IconButton, Tooltip, CircularProgress,
    FormControl, Select, MenuItem, Typography, Box,
    Button, Chip,
} from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import FilterListIcon from '@mui/icons-material/FilterList';
import dayjs from 'dayjs';
import MasterCalendar from '../../VisitScheduled/MasterCalendar';
import { THEME_COLOR } from '../../../theme/formStyles';
import Filter from '../../Common/Filter';
import { updateParams } from '../../../utils';

const MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
];

const SCHEDULE_TYPES = [
    { value: 'all',         label: 'All' },
    { value: 'calibration', label: 'Calibration',   bg: '#fff3e0', color: '#c75000', border: '#ffcc80' },
    { value: 'opportunity', label: 'BD Client Meet', bg: '#f0ebff', color: '#6a3db5', border: '#c9b8f0' },
    { value: 'lead',        label: 'Lead Meet',      bg: '#e0f7fa', color: '#00695c', border: '#80cbc4' },
    { value: 'project',     label: 'Project Task',   bg: '#e3f2fd', color: '#1565c0', border: '#90caf9' },
    { value: 'ticket',      label: 'Ticket',         bg: '#fce4ec', color: '#b71c1c', border: '#ef9a9a' },
];

export default function MasterSchedules() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const today = dayjs();

    const [currentMonth, setCurrentMonth] = useState(today.month());
    const [currentYear, setCurrentYear] = useState(today.year());
    const [schedules, setSchedules] = useState([]);
    const [opportunitySchedules, setOpportunitySchedules] = useState([]);
    const [leadSchedules, setLeadSchedules] = useState([]);
    const [projectSubTasks, setProjectSubTasks] = useState([]);
    const [ticketSchedules, setTicketSchedules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [facilities, setFacilities] = useState([]);
    const [greenUsers, setGreenUsers] = useState([]);
    const [showFilterDialog, setShowFilterDialog] = useState(false);
    const [isSearchOrFilterApplied, setIsSearchOrFilterApplied] = useState(false);
    const [queryParams] = useSearchParams();

    const existingParams = Object.fromEntries([...queryParams]);
    const scheduleType = existingParams.schedule_type || 'all';

    useEffect(() => {
        dispatch(getAllFacilitiesForTickets()).then(resp => {
            if (resp?.data?.success) setFacilities(resp.data.allData || []);
        });
        dispatch(getJoinedGreenUsers()).then(resp => {
            if (resp?.data?.success) {
                setGreenUsers(resp.data.allData?.filter(u => u.status !== 'Inactive') || []);
            }
        });
    }, []);

    useEffect(() => {
        fetchSchedules();
    }, [currentMonth, currentYear, queryParams]);

    const fetchSchedules = () => {
        setLoading(true);
        const calParams  = { month: currentMonth + 1, year: currentYear, ...existingParams };
        const oppParams  = { month: currentMonth + 1, year: currentYear, ...existingParams };
        const leadParams = { month: currentMonth + 1, year: currentYear, ...existingParams };
        const projParams = { month: currentMonth + 1, year: currentYear, ...existingParams };
        const tktParams  = { month: currentMonth + 1, year: currentYear, ...existingParams };
        Promise.all([
            dispatch(getCalibrationSchedulesCalendar(calParams)),
            dispatch(getOpportunitySchedulesCalendar(oppParams)),
            dispatch(getLeadSchedulesCalendar(leadParams)),
            dispatch(getProjectSubTasksCalendar(projParams)),
            dispatch(getTicketsCalendar(tktParams)),
        ]).then(([calResp, oppResp, leadResp, projResp, tktResp]) => {
            setSchedules(calResp?.data?.success ? calResp.data.data || [] : []);
            setOpportunitySchedules(oppResp?.data?.success ? oppResp.data.data || [] : []);
            setLeadSchedules(leadResp?.data?.success ? leadResp.data.data || [] : []);
            setProjectSubTasks(projResp?.data?.success ? projResp.data.data || [] : []);
            setTicketSchedules(tktResp?.data?.success ? tktResp.data.data || [] : []);
            setLoading(false);
        }).catch(() => {
            setSchedules([]);
            setOpportunitySchedules([]);
            setLeadSchedules([]);
            setProjectSubTasks([]);
            setTicketSchedules([]);
            setLoading(false);
        });
    };

    // Resolve the selected assignee's display name for client-side filtering
    const assigneeFilterName = useMemo(() => {
        const email = existingParams.assignee_name;
        if (!email || email === 'all') return null;
        return greenUsers.find(u => u.user_id === email)?.name || null;
    }, [existingParams.assignee_name, greenUsers]);

    const getCalAssigneeName = (s) => {
        const a = s.assignee_name;
        if (!a) return '';
        if (typeof a === 'object') return a.name || '';
        try { return JSON.parse(a)?.name || a; } catch { return a; }
    };
    const getSpocDisplayName = (sp) => {
        if (!sp) return '';
        if (typeof sp === 'object') return sp.name || '';
        if (sp === '[object Object]') return '';
        try { return JSON.parse(sp)?.name || sp; } catch { return sp; }
    };

    const visibleCalibrationSchedules = useMemo(() => {
        if (scheduleType !== 'all' && scheduleType !== 'calibration') return [];
        if (!assigneeFilterName) return schedules;
        return schedules.filter(s => getCalAssigneeName(s) === assigneeFilterName);
    }, [schedules, scheduleType, assigneeFilterName]);

    const visibleOpportunitySchedules = useMemo(() => {
        if (scheduleType !== 'all' && scheduleType !== 'opportunity') return [];
        if (!assigneeFilterName) return opportunitySchedules;
        return opportunitySchedules.filter(s => (s.bd_owner?.name || '') === assigneeFilterName);
    }, [opportunitySchedules, scheduleType, assigneeFilterName]);

    const visibleLeadSchedules = useMemo(() => {
        if (scheduleType !== 'all' && scheduleType !== 'lead') return [];
        if (!assigneeFilterName) return leadSchedules;
        return leadSchedules.filter(s => (s.bd_owner?.name || '') === assigneeFilterName);
    }, [leadSchedules, scheduleType, assigneeFilterName]);

    const visibleProjectSubTasks = useMemo(() => {
        if (scheduleType !== 'all' && scheduleType !== 'project') return [];
        if (!assigneeFilterName) return projectSubTasks;
        return projectSubTasks.filter(s => getSpocDisplayName(s.spoc) === assigneeFilterName);
    }, [projectSubTasks, scheduleType, assigneeFilterName]);

    const visibleTicketSchedules = useMemo(() => {
        if (scheduleType !== 'all' && scheduleType !== 'ticket') return [];
        if (!existingParams.assignee_name || existingParams.assignee_name === 'all') return ticketSchedules;
        return ticketSchedules.filter(s => s.assignee === existingParams.assignee_name);
    }, [ticketSchedules, scheduleType, existingParams.assignee_name]);

    const updateScheduleType = (value) => {
        const params = new URLSearchParams(existingParams);
        if (value === 'all') params.delete('schedule_type'); else params.set('schedule_type', value);
        navigate({ search: params.toString() }, { replace: true });
    };

    const updateQueryParams = (params) => {
        const searchParams = updateParams(existingParams, params);
        navigate({ search: searchParams.toString() }, { replace: true });
    };

    const goToToday = () => { setCurrentMonth(today.month()); setCurrentYear(today.year()); };
    const goToPrevMonth = () => {
        if (currentMonth === 0) { setCurrentMonth(11); setCurrentYear(y => y - 1); }
        else setCurrentMonth(m => m - 1);
    };
    const goToNextMonth = () => {
        if (currentMonth === 11) { setCurrentMonth(0); setCurrentYear(y => y + 1); }
        else setCurrentMonth(m => m + 1);
    };

    const yearOptions = [];
    for (let y = today.year() - 2; y <= today.year() + 5; y++) yearOptions.push(y);

    return (
        <Box>
            {/* Header */}
            <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Grid item>
                    <Typography variant="h5" sx={{ fontWeight: 600, color: '#333' }}>
                        Master Schedules
                    </Typography>
                </Grid>
                <Grid item>
                    <IconButton onClick={() => setShowFilterDialog(true)} title="Apply Filter">
                        <FilterListIcon />
                    </IconButton>
                </Grid>
            </Grid>

            {/* Assignee / Facility filter chips (existing Filter component) */}
            <Box sx={{ mb: 2 }}>
                <Filter
                    filterArea="VisitScheduled"
                    showFilterDialog={showFilterDialog}
                    setShowFilterDialog={setShowFilterDialog}
                    updateQueryParams={updateQueryParams}
                    queryParams={queryParams}
                    setIsSearchOrFilterApplied={setIsSearchOrFilterApplied}
                />
            </Box>

            {/* Calendar Navigation Bar */}
            <Grid container alignItems="center" sx={{
                py: 1.5, px: 2,
                backgroundColor: '#fff',
                borderRadius: '8px 8px 0 0',
                borderBottom: '1px solid #e0e0e0',
            }}>
                <Grid item sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Tooltip title="Calendar view">
                        <IconButton size="small" sx={{ color: THEME_COLOR }}>
                            <CalendarTodayIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Button size="small" variant="outlined" onClick={goToToday} sx={{
                        borderColor: THEME_COLOR, color: THEME_COLOR,
                        textTransform: 'none', fontWeight: 600, minWidth: 'auto', px: 2,
                        '&:hover': { borderColor: '#006666', backgroundColor: 'rgba(0,128,128,0.04)' },
                    }}>
                        Today
                    </Button>
                    <IconButton size="small" onClick={goToPrevMonth} sx={{ color: '#555' }}>
                        <KeyboardArrowUpIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={goToNextMonth} sx={{ color: '#555' }}>
                        <KeyboardArrowDownIcon fontSize="small" />
                    </IconButton>
                </Grid>
                <Grid item sx={{ ml: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FormControl size="small" variant="standard" sx={{ minWidth: 120 }}>
                        <Select value={currentMonth} onChange={e => setCurrentMonth(parseInt(e.target.value))}
                            disableUnderline sx={{ fontWeight: 600, fontSize: '16px', color: '#333' }}
                            MenuProps={{ disableScrollLock: true }}>
                            {MONTHS.map((m, idx) => <MenuItem key={idx} value={idx}>{m}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <FormControl size="small" variant="standard" sx={{ minWidth: 80 }}>
                        <Select value={currentYear} onChange={e => setCurrentYear(parseInt(e.target.value))}
                            disableUnderline sx={{ fontWeight: 600, fontSize: '16px', color: '#333' }}
                            MenuProps={{ disableScrollLock: true }}>
                            {yearOptions.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                        </Select>
                    </FormControl>
                </Grid>
            </Grid>

            {/* Schedule Type filter */}
            <Box sx={{
                display: 'flex', alignItems: 'center', gap: 1,
                px: 2, py: 1,
                backgroundColor: '#fafafa',
                borderBottom: '1px solid #e0e0e0',
            }}>
                <Typography sx={{ fontSize: '12px', color: '#777', fontWeight: 500, mr: 0.5 }}>
                    Type:
                </Typography>
                {SCHEDULE_TYPES.map(({ value, label, bg, color, border }) => {
                    const active = scheduleType === value;
                    return (
                        <Chip
                            key={value}
                            label={label}
                            size="small"
                            onClick={() => updateScheduleType(value)}
                            sx={{
                                cursor: 'pointer',
                                fontWeight: active ? 600 : 400,
                                fontSize: '12px',
                                backgroundColor: active ? (bg || THEME_COLOR) : '#fff',
                                color: active ? (color || '#fff') : '#555',
                                border: `1px solid ${active ? (border || THEME_COLOR) : '#d0d0d0'}`,
                                '&:hover': { filter: 'brightness(0.95)' },
                            }}
                        />
                    );
                })}
            </Box>

            {/* Calendar Grid */}
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px', backgroundColor: '#fff', borderRadius: '0 0 8px 8px' }}>
                    <CircularProgress sx={{ color: THEME_COLOR }} />
                </Box>
            ) : (
                <MasterCalendar
                    month={currentMonth}
                    year={currentYear}
                    schedules={visibleCalibrationSchedules}
                    opportunitySchedules={visibleOpportunitySchedules}
                    leadSchedules={visibleLeadSchedules}
                    projectSubTasks={visibleProjectSubTasks}
                    ticketSchedules={visibleTicketSchedules}
                />
            )}
        </Box>
    );
}
